import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { SystemSettings as SystemSettingsType } from '../../types/admin';

export const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettingsType>({
    aiUsageLimit: 50,
    maintenanceMode: false,
    allowNewRegistrations: true,
    sessionTimeout: 60,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingSettings, setPendingSettings] = useState<SystemSettingsType | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/system/settings');
      setSettings(response.data);
    } catch (error) {
      setError('システム設定の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key: keyof SystemSettingsType, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    // Clear success message when user makes changes
    setSuccess(null);
  };

  const handleSave = async () => {
    // Show confirmation for critical settings
    if (settings.maintenanceMode || !settings.allowNewRegistrations) {
      setPendingSettings(settings);
      setConfirmDialog(true);
      return;
    }
    
    await saveSettings(settings);
  };

  const saveSettings = async (settingsToSave: SystemSettingsType) => {
    setSaving(true);
    setError(null);
    
    try {
      await axios.put('/api/admin/system/settings', settingsToSave);
      setSuccess('システム設定を保存しました');
      setConfirmDialog(false);
      setPendingSettings(null);
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || 'システム設定の保存に失敗しました'
        : 'システム設定の保存に失敗しました';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchSettings();
    setSuccess(null);
    setError(null);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        システム設定
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* AI利用制限設定 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SpeedIcon sx={{ color: 'primary.main', mr: 2 }} />
                <Typography variant="h6">AI利用制限</Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                ユーザーが1日に利用できるAI回数の上限を設定します。
              </Typography>
              
              <TextField
                label="1日の利用上限回数"
                type="number"
                value={settings.aiUsageLimit}
                onChange={(e) => handleSettingChange('aiUsageLimit', parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 1, max: 1000 }}
                helperText="推奨値: 50回/日"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* セッション設定 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ color: 'secondary.main', mr: 2 }} />
                <Typography variant="h6">セッション設定</Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                ユーザーセッションのタイムアウト時間を設定します。
              </Typography>
              
              <TextField
                label="セッションタイムアウト（分）"
                type="number"
                value={settings.sessionTimeout}
                onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value) || 30)}
                fullWidth
                inputProps={{ min: 15, max: 480 }}
                helperText="推奨値: 60分"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* システム運用設定 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SettingsIcon sx={{ color: 'warning.main', mr: 2 }} />
                <Typography variant="h6">システム運用設定</Typography>
              </Box>
              
              <Stack spacing={3}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.maintenanceMode}
                        onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                        color="warning"
                      />
                    }
                    label="メンテナンスモード"
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                    有効にすると一般ユーザーのシステム利用を制限します
                  </Typography>
                  {settings.maintenanceMode && (
                    <Chip 
                      label="⚠️ メンテナンスモード有効" 
                      color="warning" 
                      size="small" 
                      sx={{ ml: 4, mt: 1 }} 
                    />
                  )}
                </Box>

                <Divider />

                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.allowNewRegistrations}
                        onChange={(e) => handleSettingChange('allowNewRegistrations', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="新規ユーザー登録を許可"
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                    無効にすると新規ユーザーの登録を停止します
                  </Typography>
                  {!settings.allowNewRegistrations && (
                    <Chip 
                      label="🚫 新規登録停止中" 
                      color="error" 
                      size="small" 
                      sx={{ ml: 4, mt: 1 }} 
                    />
                  )}
                </Box>
              </Stack>
            </CardContent>

            <CardActions sx={{ px: 3, pb: 3 }}>
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  startIcon={<SaveIcon />}
                  disabled={saving || loading}
                  sx={{ flex: 1 }}
                >
                  {saving ? '保存中...' : '設定を保存'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RestartAltIcon />}
                  disabled={saving || loading}
                >
                  リセット
                </Button>
              </Stack>
            </CardActions>
          </Card>
        </Grid>

        {/* 現在の設定状況 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              現在の設定状況
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  AI利用上限
                </Typography>
                <Typography variant="h6" color="primary">
                  {settings.aiUsageLimit}回/日
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  セッション時間
                </Typography>
                <Typography variant="h6" color="secondary">
                  {settings.sessionTimeout}分
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  メンテナンス
                </Typography>
                <Typography variant="h6" color={settings.maintenanceMode ? 'warning.main' : 'success.main'}>
                  {settings.maintenanceMode ? '有効' : '無効'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  新規登録
                </Typography>
                <Typography variant="h6" color={settings.allowNewRegistrations ? 'success.main' : 'error.main'}>
                  {settings.allowNewRegistrations ? '許可' : '停止'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* 確認ダイアログ */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>設定変更の確認</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            以下の重要な設定を変更しようとしています：
          </Typography>
          <Box sx={{ mt: 2 }}>
            {pendingSettings?.maintenanceMode && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                メンテナンスモードを有効にします。一般ユーザーのアクセスが制限されます。
              </Alert>
            )}
            {pendingSettings && !pendingSettings.allowNewRegistrations && (
              <Alert severity="error" sx={{ mb: 1 }}>
                新規ユーザー登録を停止します。新しいユーザーは登録できなくなります。
              </Alert>
            )}
          </Box>
          <Typography sx={{ mt: 2 }}>
            この変更を実行してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={() => pendingSettings && saveSettings(pendingSettings)} 
            variant="contained"
            color="warning"
            disabled={saving}
          >
            実行
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};